// Selection toolbar that appears alongside the bulk-category toolbar
// when 1+ materials are checked. Lets the operator soft-delete or
// restore the entire selection in one shot. The toolbar is purely an
// event emitter — the parent owns the confirm modal and the actual
// bulk write, so the same `ManufacturingMaterialDeleteConfirm` modal
// can fan out for single-row + bulk paths and the working / error
// state lives in one place (the modal).
//
// Mirrors the visual chrome of `ManufacturingBulkCategoryToolbar` so
// the two toolbars stack cleanly under the materials list.
//
// Dev-sync 184bc79f.

type Props = {
  selectedIds: ReadonlyArray<string>;
  /** True when ALL selected ids point at materials that are
   *  currently active. Drives whether we expose Delete (and hide
   *  Restore) — an all-active selection is the common path. */
  allSelectedActive: boolean;
  /** True when ALL selected ids point at materials that are
   *  currently inactive. Drives whether we expose Restore (and hide
   *  Delete). Mixed selections show neither (caller hint copy). */
  allSelectedInactive: boolean;
  busy: boolean;
  onClear: () => void;
  /** Parent opens its confirm modal; once confirmed, parent runs the
   *  bulk soft-delete + clears selection + toasts the summary. */
  onRequestBulkDelete: () => void;
  onRequestBulkRestore: () => void;
};

const clearBtnClass =
  "rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700";

const dangerBtnClass =
  "rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:hover:bg-amber-900/40";

const restoreBtnClass =
  "rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-900 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200 dark:hover:bg-emerald-900/40";

export function ManufacturingBulkDeleteToolbar({
  selectedIds,
  allSelectedActive,
  allSelectedInactive,
  busy,
  onClear,
  onRequestBulkDelete,
  onRequestBulkRestore,
}: Props) {
  const showDelete = allSelectedActive;
  const showRestore = allSelectedInactive;
  const showMixedHint = !showDelete && !showRestore;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/60 p-2 dark:border-amber-900 dark:bg-amber-950/15">
      <span className="text-xs font-semibold text-amber-900 dark:text-amber-200">
        {selectedIds.length} selected
      </span>
      <button type="button" className={clearBtnClass} onClick={onClear} disabled={busy}>
        Clear
      </button>
      {showDelete ? (
        <button
          type="button"
          className={dangerBtnClass}
          onClick={onRequestBulkDelete}
          disabled={busy}
        >
          {`Delete ${selectedIds.length} selected`}
        </button>
      ) : null}
      {showRestore ? (
        <button
          type="button"
          className={restoreBtnClass}
          onClick={onRequestBulkRestore}
          disabled={busy}
        >
          {`Restore ${selectedIds.length} selected`}
        </button>
      ) : null}
      {showMixedHint ? (
        <span className="text-[11px] text-amber-900/80 dark:text-amber-200/80">
          Mixed active + inactive selection — clear or pick one tier to enable bulk
          delete / restore.
        </span>
      ) : null}
    </div>
  );
}
